import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { APP_SETTINGS } from '@constants/constants';
import { getAddressFromAutoCompleteSuggestion } from '@helpers/address';
import { useDebounceValue } from '@hooks/useDebounce';
import usePrevious from '@hooks/usePrevious';

import './AddressAutoComplete.scss';

const googleMapsLibrariesToBeLoaded: Libraries = ['places'];

interface Props {
  value: string;
  focused: boolean;
  onChange: (street: string, city?: string, region?: string, postal?: string) => void;
}

export interface AddressAutoCompleteMethods {
  select: () => void;
  onArrowKey: (offset: number) => void;
}

const AddressAutoComplete = forwardRef<AddressAutoCompleteMethods, Props>((props, ref) => {
  const { value: fieldValue, focused, onChange } = props;
  const [dropdownFocused, setDropdownFocused] = useState(false);
  const value = useDebounceValue(fieldValue);
  const prevValue = usePrevious(value);
  const [placeSuggestions, setPlaceSuggestions] = useState<any[]>([]);
  const [suggestedStreet, setSuggestedStreet] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: APP_SETTINGS.googleMapsApiKey,
    libraries: googleMapsLibrariesToBeLoaded,
  });

  // This method will be exposed to the parent via ref
  useImperativeHandle(ref, () => ({
    select: () => {
      if (placeSuggestions[selectedIndex]) {
        handlePlaceSelect(placeSuggestions[selectedIndex]);
      }
    },
    onArrowKey: (offset: number) => {
      if (placeSuggestions.length) {
        setSelectedIndex((selectedIndex + placeSuggestions.length + offset) % placeSuggestions.length);
      }
    },
  }));

  useEffect(() => {
    if (!isLoaded || !value || value === prevValue || value === suggestedStreet) return;

    const loadAddress = async () => {
      const sessionToken = new window.google.maps.places.AutocompleteSessionToken();

      // Define request options.
      const request = {
        input: value,
        sessionToken,
      };

      // Perform the query and display the results.
      const { suggestions } =
        // @ts-ignore
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      setPlaceSuggestions(suggestions);
      setSelectedIndex(-1);
    };

    loadAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, value, prevValue, suggestedStreet]);

  const handlePlaceSelect = async (suggestion: any) => {
    const result = await getAddressFromAutoCompleteSuggestion(suggestion);
    if (result) {
      setSuggestedStreet(result.streetAddress);
      setPlaceSuggestions([]);

      onChange(result.streetAddress, result.city, result.region, result.postalCode);
    }
  };

  if ((!focused && !dropdownFocused) || !fieldValue || !placeSuggestions.length) return null;

  return (
    <ul
      className='custom-address-autocomplete'
      role='listbox'
      onFocus={() => setDropdownFocused(true)}
      onBlur={() => setDropdownFocused(false)}
    >
      {placeSuggestions.map((s, index) => (
        <li
          className={classNames('autocpt-item', selectedIndex === index && 'active')}
          role='menuitem'
          key={s.placePrediction.text.text}
          onMouseDown={() => handlePlaceSelect(s)}
        >
          <span>{s.placePrediction.text.text}</span>
        </li>
      ))}
    </ul>
  );
});

AddressAutoComplete.displayName = 'AddressAutoComplete';

export default AddressAutoComplete;
